export interface AnimationConfig {
    duration: number;
    delay: number;
    ease: string;
    type: string;
    fill?: 'forwards' | 'none';
}

export interface AnimationConfigs {
    coverImage: AnimationConfig;
    galleryName: AnimationConfig;
    studioName: AnimationConfig;
    viewBtn: AnimationConfig;
    line?: AnimationConfig;
    gradient?: AnimationConfig;
    background?: AnimationConfig;
    lines?: AnimationConfig;
    border?: AnimationConfig;
    outline?: AnimationConfig;
}

export const animationVariants = {
    none: {
        initial: {},
        animate: {}
    },
    fadeIn: {
        initial: { opacity: 0 },
        animate: { opacity: 1 }
    },
    fadeInUp: {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 }
    },
    fadeInDown: {
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 }
    },
    fadeInLeft: {
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0 }
    },
    fadeInRight: {
        initial: { opacity: 0, x: 20 },
        animate: { opacity: 1, x: 0 }
    },
    scaleUp: {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 }
    },
    scaleDown: {
        initial: { opacity: 0, scale: 1.2 },
        animate: { opacity: 1, scale: 1 }
    },
    slideInLeft: {
        initial: { x: -100, opacity: 0 },
        animate: { x: 0, opacity: 1 }
    },
    slideInRight: {
        initial: { x: 100, opacity: 0 },
        animate: { x: 0, opacity: 1 }
    },
    slideInUp: {
        initial: { y: 100, opacity: 0 },
        animate: { y: 0, opacity: 1 }
    },
    slideInDown: {
        initial: { y: -100, opacity: 0 },
        animate: { y: 0, opacity: 1 }
    },
    zoomIn: {
        initial: { scale: 0, opacity: 0 },
        animate: { scale: 1, opacity: 1 }
    },
    flipX: {
        initial: { rotateX: -90, opacity: 0 },
        animate: { rotateX: 0, opacity: 1 }
    },
    flipY: {
        initial: { rotateY: -90, opacity: 0 },
        animate: { rotateY: 0, opacity: 1 }
    },
    rotateIn: {
        initial: { rotate: -180, opacity: 0 },
        animate: { rotate: 0, opacity: 1 }
    }
};

export const borderThickAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    border: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'none' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInDown' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' }
} as const;

export const borderLineAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    outline: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'none' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInUp' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' }
} as const;

export const linesAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    lines: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'scaleUp' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInDown' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' }
} as const;

export const squareAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInUp' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'fadeInUp' }
} as const;

export const detailRightAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInRight' },
    line: { duration: 0.6, delay: 0.5, ease: 'easeOut', type: 'fadeIn' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInRight' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInRight' }
} as const;

export const detailLeftAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInLeft' },
    line: { duration: 0.6, delay: 0.5, ease: 'easeOut', type: 'fadeIn' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInLeft' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInLeft' }
} as const;

export const block2LeftAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'slideInLeft' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInRight' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInRight' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInRight' }
} as const;

export const block2RightAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'slideInRight' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInLeft' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInLeft' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInLeft' }
} as const;

export const blockLeftAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'slideInLeft' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInRight' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInRight' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInRight' }
} as const;

export const blockRightAnimations: AnimationConfigs = {
    coverImage: { duration: 0.8, delay: 0.2, ease: 'easeOut', type: 'slideInRight' },
    galleryName: { duration: 0.8, delay: 0.4, ease: 'easeOut', type: 'slideInLeft' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInLeft' },
    viewBtn: { duration: 0.8, delay: 0.8, ease: 'easeOut', type: 'slideInLeft' }
} as const;

export const boxLeftAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' },
    background: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'slideInLeft' }
} as const;

export const boxRightAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    studioName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' },
    background: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'slideInRight' }
} as const;

export const simpleRightAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInRight' },
    studioName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'slideInRight' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'slideInRight' },
    gradient: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'slideInUp' }
} as const;

export const simpleLeftAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'slideInLeft' },
    studioName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'slideInLeft' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'slideInLeft' },
    gradient: { duration: 0.8, delay: 0.3, ease: 'easeOut', type: 'slideInUp' }
} as const;

export const bannerAnimations: AnimationConfigs = {
    coverImage: { duration: 1, delay: 0, ease: 'easeOut', type: 'fadeIn' },
    galleryName: { duration: 0.8, delay: 0.6, ease: 'easeOut', type: 'fadeInUp' },
    studioName: { duration: 0.8, delay: 0.7, ease: 'easeOut', type: 'fadeInUp' },
    viewBtn: { duration: 0.8, delay: 0.9, ease: 'easeOut', type: 'fadeInUp' }
} as const;

export const getAnimationProps = (config: AnimationConfig, enableAnimations = true) => {
    if (!enableAnimations) {
        return {
            initial: false,
            animate: false
        };
    }

    const variant = animationVariants[config.type as keyof typeof animationVariants];
    return {
        initial: variant.initial,
        animate: variant.animate,
        transition: {
            duration: config.duration,
            delay: config.delay
        }
    };
};
